import { useState, type FC } from "react";
import { ICON_BASE_PATH } from "@/app/utils/config";
import {
  GxLineStepper,
  type GxLineStepperStep,
} from "@/app/components/common/actions/Stepper/GxLineStepper";
import AddressCommon from "@/app/components/common/actions/inputs-b/AddressCommon";
import CountryCode from "@/app/components/common/actions/inputs-b/CountryCode";
import Landline from "@/app/components/common/actions/inputs-b/Landline";
import FirstName from "@/app/components/common/actions/inputs-b/FirstName";
import DatePickerInput from "@/app/components/common/actions/inputs-b/DatePickerInput";
import LicenseUpload from "@/app/components/common/actions/inputs-b/LicenseUpload";
import Mobilefiled from "@/app/components/common/actions/inputs-b/Mobilefiled";
import { Switch, type SwitchChangeEvent } from "@progress/kendo-react-inputs";
import type { ProfileFormData } from "./types";

interface FileUploadChangeEvent {
  value: File | null;
}

export type ProfileSubStep = 0 | 1 | 2 | 3 ;

interface ProfileStepProps {
  data: ProfileFormData;
  updateField: (field: keyof ProfileFormData, value: any) => void;
  subStep: ProfileSubStep;
  setSubStep: (step: ProfileSubStep) => void;
}

const PROFILE_SUBSTEPS: GxLineStepperStep[] = [
  { id: "personal", label: "Personal Info" },
  { id: "address", label: "Company Address" },
  { id: "trade-license", label: "Trade License" },
  { id: "contact", label: "Contact Details" },
];

// Dummy demo values — only used as a fallback when the real field is empty,
// so each step renders populated content without needing live data wired up.
const DUMMY_PROFILE_DATA = {
  firstName: "Ahmed",
  lastName: "Al Mansouri",
  mobileDialCode: "+971",
  mobileNumber: "501234567",
  email: "ahmed.almansouri@example.com",

  addressLine1: "Building 12, Al Maktoum Street",
  addressLine2: "Deira",
  addressLine3: "Dubai",
  addressLine4: "United Arab Emirates",
  countryCode2: "AE",

  tradeLicense: "CN-1234567",
  tradeLicenseCurrentEndDate: "2026-12-31",
  tradeLicenseNewEndDate: "2027-12-31",

  companyLandline: "+971 4 123 4567",
  companyPoBox: "PO Box 45678",

  whatsappNumber: "501234567",
  whatsappDialCode: "+971",
};

const ProfileStep: FC<ProfileStepProps> = ({
  data,
  updateField,
  subStep,
  setSubStep,
}) => {
  const [showMobile, setShowMobile] = useState(false);

  const firstName = data.firstName || DUMMY_PROFILE_DATA.firstName;
  const lastName = data.lastName || DUMMY_PROFILE_DATA.lastName;
  const mobileDialCode =
    data.mobileDialCode || DUMMY_PROFILE_DATA.mobileDialCode;
  const mobileNumber = data.mobileNumber || DUMMY_PROFILE_DATA.mobileNumber;
  const email = data.email || DUMMY_PROFILE_DATA.email;

  const maskedMobile = `${mobileDialCode} ••• ••• ${mobileNumber.slice(-3)}`;

  const goBack = () => {
    if (subStep > 0) setSubStep((subStep - 1) as ProfileSubStep);
  };

  const handleWhatsappToggle = (e: SwitchChangeEvent) => {
    updateField("whatsappOptIn", e.value);
  };

  const handleTradeLicenseFileChange = (event: FileUploadChangeEvent): void => {
    const file = event.value;
    if (!file) return;

    updateField("tradeLicenseFile", file);
    updateField("tradeLicenseFileName", file.name);
  };

  return (
    <div className="space-y-6">
      <GxLineStepper
        steps={PROFILE_SUBSTEPS}
        currentStep={subStep}
        showStepCounter
        barHeight={10}
      />

      {subStep > 0 && (
        <button className="gx-p12-sb mb-2" onClick={goBack}>
          ← Back
        </button>
      )}

      {/* .....................personal info........................ */}
      {subStep === 0 && (
        <div className="gx-profile-tab-container">
          <div className="gx-profile-title">
            <h3 className="gx-h3-b">Personal Information</h3>
          </div>

          <div className="information-list">
            <div className="information-list-child">
              <div className="information-list-img">
                <img src={`${ICON_BASE_PATH}userprofile.svg`} alt="User" />
              </div>
              <p className="gx-p12-sb">
                Applicant Name
                <span className="gx-p14-sb">
                  {firstName} {lastName}
                </span>
              </p>
            </div>
          </div>

          <div className="information-list">
            <div className="information-list-child">
              <div className="information-list-img">
                <img src={`${ICON_BASE_PATH}mobile.svg`} alt="Mobile" />
              </div>
              <div className="information-list-mobile">
                <p className="gx-p12-sb">
                  Mobile Number
                  <span className="gx-p14-sb">
                    {showMobile
                      ? `${mobileDialCode} ${mobileNumber}`
                      : maskedMobile}
                  </span>
                </p>
                <button
                  type="button"
                  onClick={() => setShowMobile((v) => !v)}
                  aria-label={
                    showMobile ? "Hide mobile number" : "Show mobile number"
                  }
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  <img
                    src={`${ICON_BASE_PATH}${showMobile ? "eyeopen.svg" : "eyeclose.svg"}`}
                    alt=""
                  />
                </button>
              </div>
            </div>
          </div>

          <div className="information-list">
            <div className="information-list-child">
              <div className="information-list-img">
                <img src={`${ICON_BASE_PATH}email.svg`} alt="Email" />
              </div>
              <p className="gx-p12-sb">
                Email ID
                <span className="gx-p14-md">{email}</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* .....................company address........................ */}
      {subStep === 1 && (
        <div className="gx-profile-tab-container">
          <div className="gx-profile-title">
            <h3 className="gx-h3-b">Company Address</h3>
          </div>

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12">
              <AddressCommon
                label="Address 1"
                value={data.addressLine1 || DUMMY_PROFILE_DATA.addressLine1}
                onChange={(val) => updateField("addressLine1", val)}
              />
            </div>
            <div className="col-span-12">
              <AddressCommon
                label="Address 2"
                value={data.addressLine2 || DUMMY_PROFILE_DATA.addressLine2}
                onChange={(val) => updateField("addressLine2", val)}
              />
            </div>
            <div className="col-span-12">
              <AddressCommon
                label="Address 3"
                value={data.addressLine3 || DUMMY_PROFILE_DATA.addressLine3}
                onChange={(val) => updateField("addressLine3", val)}
              />
            </div>
            <div className="col-span-12">
              <AddressCommon
                label="Address 4"
                value={data.addressLine4 || DUMMY_PROFILE_DATA.addressLine4}
                onChange={(val) => updateField("addressLine4", val)}
              />
            </div>
            <div className="col-span-12">
              <CountryCode
                label="Country Code"
                value={data.countryCode2 || DUMMY_PROFILE_DATA.countryCode2}
                onChange={(val) => updateField("countryCode2", val)}
              />
            </div>
          </div>
        </div>
      )}

      {/* .....................trade license........................ */}
      {subStep === 2 && (
        <div className="gx-profile-tab-container">
          <div className="gx-profile-title">
            <h3 className="gx-h3-b">Trade License</h3>
          </div>

          <div className="flex flex-wrap -mx-2 gx-row-bottom">
            <div className="w-full md:w-1/2 px-2">
              <label className="gx-p12-sb block mb-1">
                Trade license number
              </label>
              <input
                className="gx-input"
                value={data.tradeLicense || DUMMY_PROFILE_DATA.tradeLicense}
                onChange={(e) => updateField("tradeLicense", e.target.value)}
              />
            </div>
            <div className="w-full md:w-1/2 px-2">
              <DatePickerInput
                label="Current End Date"
                value={
                  data.tradeLicenseCurrentEndDate ||
                  DUMMY_PROFILE_DATA.tradeLicenseCurrentEndDate
                }
                onChange={(val) =>
                  updateField("tradeLicenseCurrentEndDate", val || "")
                }
                iconSrc={`${ICON_BASE_PATH}date.svg`}
              />
            </div>
          </div>

          <div className="flex flex-wrap -mx-2 gx-row-bottom mt-4">
            <div className="w-full md:w-1/2 px-2">
              <DatePickerInput
                label="New End Date"
                value={
                  data.tradeLicenseNewEndDate ||
                  DUMMY_PROFILE_DATA.tradeLicenseNewEndDate
                }
                onChange={(val) =>
                  updateField("tradeLicenseNewEndDate", val || "")
                }
                iconSrc={`${ICON_BASE_PATH}date.svg`}
              />
            </div>
          </div>

          <div className="gx-upload-license col-span-12 mt-4">
            <div className="gx-upload-license-container">
              <h3 className="gx-upload-license-title gx-p14-b">
                <span>Upload Renewed License</span>
                <span className="gx-p14-b gx-required">*</span>
              </h3>

              <span className="gx-upload-Choose-fIle gx-p12-md">
                {data.tradeLicenseFileName || "Choose FIle"}
              </span>
            </div>

            <LicenseUpload
              onChange={handleTradeLicenseFileChange}
              label="Upload Trade License"
            />
          </div>
        </div>
      )}

      {/* .....................contact details (landline + pobox)........................ */}
      {subStep === 3 && (
        <div className="gx-profile-tab-container">
          <div className="gx-profile-title">
            <h3 className="gx-h3-b">Contact Details</h3>
          </div>

          <div className="w-full md:w-1/2 px-2">
            <Landline
              label="Company Landline"
              value={data.companyLandline || DUMMY_PROFILE_DATA.companyLandline}
              onChange={(val) => updateField("companyLandline", val)}
            />
          </div>

          <div className="w-full md:w-1/2 px-2 mt-4">
            <FirstName
              label="company Po Box"
              value={data.companyPoBox || DUMMY_PROFILE_DATA.companyPoBox}
              onChange={(val) => updateField("companyPoBox", val)}
            />
          </div>

          <div className="w-full md:w-1/2 px-2 mt-4">
            <div className="gx-profile-tab-heading">
              <h3 className="">Receive important updates on Whatsapp</h3>
              <Switch
                checked={data.whatsappOptIn}
                onChange={handleWhatsappToggle}
              />
            </div>
            {data.whatsappOptIn && (
              <div className="mt-4">
                <Mobilefiled
                  label="Whatsapp Number"
                  value={
                    data.whatsappNumber || DUMMY_PROFILE_DATA.whatsappNumber
                  }
                  onChange={(val) => updateField("whatsappNumber", val)}
                  onValidate={() => {}}
                  countryDialCode={
                    data.whatsappDialCode || DUMMY_PROFILE_DATA.whatsappDialCode
                  }
                  onCountryCodeChange={(dialCode) =>
                    updateField("whatsappDialCode", dialCode)
                  }
                  triggerValidation={false}
                  starlable
                  disable={false}
                />
              </div>
            )}
          </div>
        </div>
      )}

    
    </div>
  );
};

export default ProfileStep;
